/**
 * Panel de Internal → Seguridad: passphrases del Vault (admin).
 *
 * Con KMS (Opción A) no hay "llave de recuperación": resetear la passphrase de
 * alguien no pierde ninguna credencial. El reset borra el hash, invalida las
 * sesiones desbloqueadas de esa persona, le avisa por Slack y queda en el log
 * del Vault (`passphrase_reset`). La persona define una nueva al entrar.
 *
 * Va por la Lambda del Vault (vaultAdmin.*): exige que el admin tenga 2SV.
 * Si no, el error se muestra explícito — nunca una tabla vacía.
 */
import { useState } from 'react';
import { KeyRound, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { usePagination } from '@/hooks/usePagination';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EmptyState, LoaderBlock, fmtDateTime } from './shared';

export function VaultProfilesCard() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const profiles = trpc.vaultAdmin.listUserProfiles.useQuery(undefined, { staleTime: 30_000, retry: false });
  const [target, setTarget] = useState<string | null>(null);

  const reset = trpc.vaultAdmin.resetUserPassphrase.useMutation({
    onSuccess: (r, vars) => {
      utils.vaultAdmin.listUserProfiles.invalidate();
      toast[r.dmSent ? 'success' : 'warning'](
        r.dmSent
          ? t('internal.securitytab.vault_reset_done', 'Passphrase reseteada y aviso enviado por Slack a {{email}}', { email: vars.email })
          : t('internal.securitytab.vault_reset_no_dm', 'Passphrase reseteada; el DM de Slack falló — avisale a {{email}}', { email: vars.email })
      );
      setTarget(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = profiles.data ?? [];
  const now = Date.now();
  const { pageItems, footer: paginationFooter } = usePagination(rows, 25);

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30">
          <KeyRound className="h-4 w-4" />
        </div>
        <div>
          <h2 className="font-semibold">{t('internal.securitytab.vault_title', 'Passphrases del Vault')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('internal.securitytab.vault_hint', 'Quién ya creó su passphrase y quién está bloqueado por intentos. Resetear no pierde ninguna credencial: la persona define una nueva al entrar, le avisamos por Slack y queda en el log del Vault.')}
          </p>
        </div>
      </div>

      {profiles.isLoading ? (
        <LoaderBlock />
      ) : profiles.isError ? (
        <EmptyState title={t('internal.securitytab.vault_error', 'No se pudo leer el Vault')} description={profiles.error.message} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('internal.securitytab.vault_empty', 'Nadie entró al Vault todavía')} />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('internal.securitytab.vault_col_person', 'Persona')}</TableHead>
                <TableHead>{t('internal.securitytab.vault_col_state', 'Estado')}</TableHead>
                <TableHead>{t('internal.securitytab.vault_col_attempts', 'Intentos fallidos')}</TableHead>
                <TableHead>{t('internal.securitytab.vault_col_last', 'Último desbloqueo')}</TableHead>
                <TableHead className="text-right">{t('internal.securitytab.vault_col_actions', 'Acciones')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((p) => {
                const locked = !!p.lockedUntil && new Date(p.lockedUntil).getTime() > now;
                return (
                  <TableRow key={p.email}>
                    <TableCell className="font-medium">{p.email}</TableCell>
                    <TableCell>
                      {locked ? (
                        <Badge variant="destructive">{t('internal.securitytab.vault_locked', 'Bloqueada hasta {{until}}', { until: fmtDateTime(p.lockedUntil as string) })}</Badge>
                      ) : p.mustReset || !p.hasPassphrase ? (
                        <Badge variant="secondary">{p.resetAt ? t('internal.securitytab.vault_reset_pending', 'Reset pendiente · {{by}}', { by: p.resetBy ?? '' }) : t('internal.securitytab.vault_no_pass', 'Sin passphrase')}</Badge>
                      ) : (
                        <Badge variant="success">{t('internal.securitytab.vault_ok', 'Activa · v{{v}}', { v: p.passphraseVersion })}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">{p.failedAttempts}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.lastUnlockAt ? fmtDateTime(p.lastUnlockAt) : '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setTarget(p.email)} disabled={reset.isPending || (!p.hasPassphrase && !locked)}>
                        <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                        {t('internal.securitytab.vault_reset', 'Resetear passphrase')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {paginationFooter}
        </div>
      )}

      <AlertDialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('internal.securitytab.vault_reset_title', '¿Resetear la passphrase de {{email}}?', { email: target ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('internal.securitytab.vault_reset_body', 'Se borra su passphrase actual, se cierran sus sesiones desbloqueadas del Vault y le llega un DM de Slack. La próxima vez que entre define una nueva. Ninguna credencial se pierde; el reset queda en el log con tu nombre.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reset.isPending}>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction disabled={reset.isPending} onClick={(e) => { e.preventDefault(); if (target) reset.mutate({ email: target }); }}>
              {reset.isPending ? t('internal.securitytab.vault_resetting', 'Reseteando…') : t('internal.securitytab.vault_reset', 'Resetear passphrase')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
